import { useEffect, useMemo, useState } from 'react';

import { Input, Select, Tabs } from 'antd';
import { ErrorMessage } from 'formik';
import { T } from 'react-translator-component';

import {
  FormInputGroupWrapper,
  FormInputWrapper,
  FormLabel,
  MessageBox,
  SelectField,
  FormSelectWrapper,
  AdvancedSettings,
  ConnectorTabs,
  PhaseRotationHint,
} from './styled';
import {
  ChargeBoxPhaseEnum,
  ChargeBoxSystemEnum,
  ChargingRateUnitEnum
} from '../../../types/responses/ChargePointGroups';
import { describePhaseRotation, phaseRotationLabel } from '../../../utils/phaseRotation/phaseRotation';

const StationConnectorsForm = ({ values, setFieldValue, handleChange, handleBlur, connectorTypesList }) => {
  const powerMax = [
    { label: '3,5 kW', value: 3500 },
    { label: '7,2 kW', value: 7200 },
    { label: '7,4 kW', value: 7400 },
    { label: '11 kW', value: 11000 },
    { label: '22 kW', value: 22000 },
    { label: '25 kW', value: 25000 },
    { label: '30 kW', value: 30000 },
    { label: '40 kW', value: 40000 },
    { label: '50 kW', value: 50000 },
    { label: '60 kW', value: 60000 },
    { label: '70 kW', value: 70000 },
    { label: '75 kW', value: 75000 },
    { label: '80 kW', value: 80000 },
    { label: '90 kW', value: 90000 },
    { label: '100 kW', value: 100000 },
    { label: '110 kW', value: 110000 },
    { label: '120 kW', value: 120000 },
    { label: '150 kW', value: 150000 },
    { label: '160 kW', value: 160000 },
    { label: '175 kW', value: 175000 },
    { label: '180 kW', value: 180000 },
    { label: '240 kW', value: 240000 },
  ];

  const [options, setOptions] = useState(filterPowerMaxBySystem(values.system, values.voltage))

  const CONNECTOR_STATUS_LIST = [
    { id: 'MAINTENANCE', name: T('Maintenance') },
    { id: 'UNAVAILABLE', name: T('Unavailable') },
    { id: 'HIDDEN', name: T('Hidden') },
  ];

  const ChargeBoxSystemLabels = {
    [ChargeBoxSystemEnum.SINGLE_PHASE]: 'Single Phase',
    [ChargeBoxSystemEnum.TWO_PHASE]: 'Two Phase',
    [ChargeBoxSystemEnum.THREE_PHASE]: 'Three Phase',
  };

  const ChargingRateUnitLabels = {
    [ChargingRateUnitEnum.A]: 'Current',
    [ChargingRateUnitEnum.W]: 'Power',
  };

  function filterPhasesBySystem(system) {
    switch (system) {
      case ChargeBoxSystemEnum.SINGLE_PHASE:
        return [ChargeBoxPhaseEnum.A, ChargeBoxPhaseEnum.B, ChargeBoxPhaseEnum.C];
      case ChargeBoxSystemEnum.TWO_PHASE:
        return [
          ChargeBoxPhaseEnum.AB,
          ChargeBoxPhaseEnum.BA,
          ChargeBoxPhaseEnum.BC,
          ChargeBoxPhaseEnum.CB,
          ChargeBoxPhaseEnum.CA,
          ChargeBoxPhaseEnum.AC,
        ];
      case ChargeBoxSystemEnum.THREE_PHASE:
        return [
          ChargeBoxPhaseEnum.ABC,
          ChargeBoxPhaseEnum.ACB,
          ChargeBoxPhaseEnum.BAC,
          ChargeBoxPhaseEnum.BCA,
          ChargeBoxPhaseEnum.CAB,
          ChargeBoxPhaseEnum.CBA,
        ];
      default:
        return [];
    }
  }

  const tensionBySystem = (system) => {
    switch (system) {
      case ChargeBoxSystemEnum.SINGLE_PHASE:
        return [{ label: '220V', value: 220 }];

      case ChargeBoxSystemEnum.TWO_PHASE:
        return [{ label: '127 / 220V', value: 220 }];

      case ChargeBoxSystemEnum.THREE_PHASE:
        return [
          { label: '127 / 220V', value: 220 },
          { label: '220 / 380V', value: 380 },
        ];

      default:
        return [];
    }
  };

  function filterPowerMaxBySystem(system, voltage) {
    switch (system) {
      case ChargeBoxSystemEnum.SINGLE_PHASE:
      case ChargeBoxSystemEnum.TWO_PHASE:
        return powerMax.filter((item) => item.value <= 7400);
      case ChargeBoxSystemEnum.THREE_PHASE:
        if (voltage && voltage === 220) {
          return powerMax.filter((item) => item.value === 11000);
        }

        return powerMax.filter((item) => item.value >= 11000);

      default:
        return powerMax;
    }
  }

  const connectors = useMemo(() => {
    if (!values?.connectors?.length) return [];

    return values.connectors.sort((a, b) => a.connectorId - b.connectorId);
  }, [values.connectors]);

  const connectorTypeSelectValues = connectorTypesList?.list?.length
    ? [{ id: null, name: T('Unspecified') }, ...connectorTypesList.list]
    : [];

  const connectorTabLabel = (connector) => {
    const typeName =
      connectorTypesList?.list?.find((type) => String(type.id) === String(connector.connectorTypeId))?.name ??
      T('Unspecified');
    return `${T('Connector')} ${connector.connectorId} · ${typeName}`;
  };

  const [activeConnector, setActiveConnector] = useState(null);
  const activeConnectorKey = connectors.some((c) => String(c.connectorId) === activeConnector)
    ? activeConnector
    : String(connectors[0]?.connectorId ?? '');

  const connectorStatusSelectValues = [{ id: null, name: T('Automatic') }, ...CONNECTOR_STATUS_LIST];

  const getConnectorStatusLabel = (statusValue) =>
    CONNECTOR_STATUS_LIST.find(({ id }) => id === statusValue) || T('Automatic');

  const user = JSON.parse(localStorage.getItem('auth-user'));

  const shouldShowAdvancedSettings = user?.permissions.some(
    (permission) => permission.route === '/virtual-groups/edit/:id/smart-charging/create' && !!permission.status,
  );

  const shouldCustomPowerMax = user?.permissions.some(
    (permission) => permission.route === '/stations/custom-powermax' && !!permission.status,
  )

  useEffect(() => {
    const filtered = filterPowerMaxBySystem(values.system, values.voltage);

    setOptions(filtered);
  }, [values.system, values.voltage]);

  return (
    <>
      {shouldShowAdvancedSettings && (
        <>
          <FormInputGroupWrapper>
            <AdvancedSettings>
              <FormLabel>{T('System')}:</FormLabel>
              <Select
                size='large'
                style={{ width: '100%' }}
                onChange={(value) => {
                  setFieldValue('phase', null);

                  setFieldValue('voltage', null);

                  values.connectors.forEach((_, index) => {
                    setFieldValue(`connectors[${index}].powerMax`, null);
                  });

                  setFieldValue('system', value);
                }}
                value={values.system}
              >
                <option value={null}>{T('Unspecified')}</option>

                {Object.keys(ChargeBoxSystemEnum).map((key) => (
                  <option value={key}>{T(ChargeBoxSystemLabels[key])}</option>
                ))}
              </Select>
            </AdvancedSettings>

            <AdvancedSettings>
              <FormLabel>{T('Phase')}:</FormLabel>
              <Select
                size='large'
                disabled={!values.system}
                style={{ width: '100%' }}
                onChange={(value) => setFieldValue('phase', value)}
                value={values.phase}
              >
                <option value={null}>{T('Unspecified')}</option>

                {filterPhasesBySystem(values.system).map((key) => (
                  <option value={key}>{phaseRotationLabel(ChargeBoxPhaseEnum[key])}</option>
                ))}
              </Select>
              {values.phase && (
                <PhaseRotationHint>{describePhaseRotation(values.phase)}</PhaseRotationHint>
              )}
            </AdvancedSettings>

            <AdvancedSettings>
              <FormLabel>{T('Tension')}:</FormLabel>
              <Select
                size='large'
                disabled={!values.system}
                style={{ width: '100%' }}
                onChange={(value) => setFieldValue('voltage', value)}
                value={values.voltage}
              >
                <option value={null}>{T('Unspecified')}</option>

                {tensionBySystem(values.system).map(({ label, value }) => (
                  <option value={value}>{label}</option>
                ))}
              </Select>
            </AdvancedSettings>

            <AdvancedSettings>
              <FormLabel>{T('Charging Rate Unit')}:</FormLabel>
              <Select
                size='large'
                disabled={!values.system}
                style={{ width: '100%' }}
                onChange={(value) => setFieldValue('chargingRateUnit', value)}
                value={values.chargingRateUnit}
              >
                <option value={null}>{T('Unspecified')}</option>

                {Object.keys(ChargingRateUnitEnum).map((key) => (
                  <option value={key}>{T(ChargingRateUnitLabels[key])}</option>
                ))}
              </Select>
            </AdvancedSettings>
          </FormInputGroupWrapper>

        </>
      )}

      <ConnectorTabs $flush={!shouldShowAdvancedSettings}>
        <Tabs activeKey={activeConnectorKey} onChange={setActiveConnector}>
        {connectors.map((connector, index) => (
          <Tabs.TabPane key={String(connector.connectorId)} tab={connectorTabLabel(connector)}>
          <FormInputGroupWrapper>
            <FormInputWrapper>
              <FormLabel>{T('Connector ID')}:</FormLabel>
              <Input
                name={`connectors[${index}].connectorId`}
                onChange={handleChange}
                onBlur={handleBlur}
                value={connector.connectorId}
                maxLength={150}
                readOnly
                disabled
              />
              <ErrorMessage name={`connectors[${index}].connectorId`} component={MessageBox} />
            </FormInputWrapper>
            <FormSelectWrapper>
              <FormLabel>{T('Connector type')}:</FormLabel>
              <SelectField
                name={`connectors[${index}].connectorTypeId`}
                as='select'
                onChange={handleChange}
                onBlur={handleBlur}
                value={connector.connectorTypeId}
              >
                {connectorTypeSelectValues.map(({ name, id }) => (
                  <option value={id}>{name}</option>
                ))}
              </SelectField>

              <ErrorMessage name={`connectors[${index}].connectorType`} component={MessageBox} />
            </FormSelectWrapper>
            <FormInputWrapper>
              <FormLabel>{T('Power Max')}:</FormLabel>

              {shouldCustomPowerMax ? (
                <Select
                  showSearch
                  style={{ width: '100%' }}
                  onChange={(value) => setFieldValue(`connectors[${index}].powerMax`, value)}
                  value={(() => connector.powerMax > 1000 ? `${connector.powerMax / 1000} kW` : connector.powerMax)()}
                  options={options}
                  onSearch={(value) => {
                    if (!options.some(option => option.label === `${value} kW`)) {
                      setOptions([...powerMax, { value: Number(value) * 1000, label: `${value} kW` }]);
                    }
                  }}
                >
                  <option value={null}>{T('Unspecified')}</option>
                </Select>
              ) : (
                <Select
                  style={{ width: '100%' }}
                  onChange={(value) => setFieldValue(`connectors[${index}].powerMax`, value)}
                  value={connector.powerMax}
                  options={options}
                >
                  <option value={null}>{T('Unspecified')}</option>
                </Select>
              )}

              <ErrorMessage name={`connectors[${index}].powerMax`} component={MessageBox} />
            </FormInputWrapper>
            <FormSelectWrapper>
              <FormLabel>{T('Connector status')}:</FormLabel>
              <SelectField
                as='select'
                name={`connectors[${index}].unusualStatus`}
                label={`${getConnectorStatusLabel(connector.unusualStatus)}`}
                onChange={handleChange}
                onBlur={handleBlur}
                value={connector.unusualStatus}
              >
                {connectorStatusSelectValues.map(({ name, id }) => (
                  <option value={id} label={name} />
                ))}
              </SelectField>

              <ErrorMessage name={`connectors[${index}].unusualStatus`} component={MessageBox} />
            </FormSelectWrapper>
          </FormInputGroupWrapper>
          </Tabs.TabPane>
        ))}
        </Tabs>
      </ConnectorTabs>
    </>
  );
};

export default StationConnectorsForm;
